import React from "react";
import { createApi } from "../../../../utils/createApi";
import { markSiteSynced } from "../../../../utils/syncStore";
import { AxiosResponse, AxiosError } from "axios";
import { GeneralInfoTab } from "../GeneralInfoTab";
import { checkIfAllCamerasDecativated } from "./checkIfAllCamerasDecativated";
import {
  AlertState,
  SiteInfo,
  SiteSchedule,
  SiteCameras,
} from "./siteAboutInterfaces";
import { NavigateFunction } from "react-router-dom";
import { checkArmedStatus } from "./checkArmedStatus";
import { universalAPIErrorHandler } from "../../../../utils/universalAPIErrorHandler";
import { getNumbersAfterAboutSite } from "../SiteAbout";
import { waitForSettingsDeployed } from "./waitForSettingsDeployed";
import { useSiteAboutStore } from "../siteAboutStore/siteAboutStore";
import { shouldOfferRebootToApply } from "../../../../utils/shouldOfferRebootToApply";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

export type HandleSyncSite = (
  setBackdropLoadingScreenOn: Dispatcher<boolean>,
  setAlertState: Dispatcher<AlertState>,
  siteInfo: SiteInfo,
  setSiteInfo: Dispatcher<SiteInfo>,
  setSyncNeeded: Dispatcher<boolean>,
  setAllSiteSchedules: Dispatcher<SiteSchedule[]>,
  setActiveTab: Dispatcher<JSX.Element>,
  vmsVariables: (string | string[] | undefined)[][],
  setSiteDeployed: Dispatcher<boolean>,
  setDeativateInquiryDialogOpen: Dispatcher<boolean>,
  setSiteCameras: Dispatcher<SiteCameras>,
  camerasNotDeployed: boolean,
  setCamerasNotDeployed: Dispatcher<boolean>,
  navigate: NavigateFunction,
  setVmsVariables: Dispatcher<(string | string[] | undefined)[][]>,
  setClickedTab: Dispatcher<string>
) => Promise<string | SiteSchedule[] | void>;

export const handleSyncSite = async (
  setBackdropLoadingScreenOn: Dispatcher<boolean>,
  setAlertState: Dispatcher<AlertState>,
  siteInfo: SiteInfo,
  setSiteInfo: Dispatcher<SiteInfo>,
  setSyncNeeded: Dispatcher<boolean>,
  setAllSiteSchedules: Dispatcher<SiteSchedule[]>,
  setActiveTab: Dispatcher<JSX.Element>,
  vmsVariables: (string | string[] | undefined)[][],
  setSiteDeployed: Dispatcher<boolean>,
  setDeativateInquiryDialogOpen: Dispatcher<boolean>,
  setSiteCameras: Dispatcher<SiteCameras>,
  camerasNotDeployed: boolean,
  setCamerasNotDeployed: Dispatcher<boolean>,
  navigate: NavigateFunction,
  setVmsVariables: Dispatcher<(string | string[] | undefined)[][]>,
  setClickedTab: Dispatcher<string>,
  setArmButtonDisabled: any,
  setArmedStatus: Dispatcher<boolean>,
  setRebootDialogOpen?: Dispatcher<boolean>
) => {
  // Deploys run async in django-q: deploy_next's 200 only means "queued", so
  // don't flip the synced state optimistically — wait for settings_deployed.
  const { setSyncPending, setSyncSiteNeeded } = useSiteAboutStore.getState();
  const AxiosUI = createApi(``);
  // siteDeployed only drives the "Site not deployed" banner — close it as
  // soon as sync is requested (including from the About Sync Site button).
  // Failure paths below set it back to false so the banner can reappear.
  setSiteDeployed(true);
  setBackdropLoadingScreenOn(true);
  const retryFunction = () => {
    handleSyncSite(
      setBackdropLoadingScreenOn,
      setAlertState,
      siteInfo,
      setSiteInfo,
      setSyncNeeded,
      setAllSiteSchedules,
      setActiveTab,
      vmsVariables,
      setSiteDeployed,
      setDeativateInquiryDialogOpen,
      setSiteCameras,
      camerasNotDeployed,
      setCamerasNotDeployed,
      navigate,
      setVmsVariables,
      setClickedTab,
      setArmButtonDisabled,
      setArmedStatus
    );
  };
  let deployNextFail = false;
  let getSiteInfoFail = false;
  let getSiteScheduleFail = false;
  let getSiteCamerasFail = false;
  let getNVRCamerasFail = false;
  localStorage.setItem("noAutoAddCamerasDialogOpen", "true");

  await AxiosUI.post(
    `customer/${getNumbersAfterAboutSite(location.href)}/action/deploy_next/`
  ).then(
    () => {
      deployNextFail = false;
      if (camerasNotDeployed) {
        setCamerasNotDeployed(false);
      }
      // Free the page while the queued deploy runs; the Sync Site button
      // shows the pending state until settings_deployed confirms.
      setBackdropLoadingScreenOn(false);
      setSyncPending(true);
    },
    (reason: AxiosError) => {
      deployNextFail = true;
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {
        setBackdropLoadingScreenOn(false);
        setSyncNeeded(true);
        setSiteDeployed(false);
        alert(`Error Syncing site, please try again.`);
      });
    }
  );
  if (!deployNextFail) {
    const { deployed } = await waitForSettingsDeployed(
      AxiosUI,
      getNumbersAfterAboutSite(location.href)
    );
    setSyncPending(false);
    if (!deployed) {
      setSyncNeeded(true);
      setSiteDeployed(false);
      alert(
        "The sync was accepted but the site still reports undeployed settings. " +
          "It may finish shortly — refresh the page to check, or click Sync Site to retry."
      );
      return;
    }
    setSyncNeeded(false);
    setSyncSiteNeeded(false);
    setSiteDeployed(true);
    markSiteSynced();
  }
  !deployNextFail &&
    (await AxiosUI.get(
      `customer/${getNumbersAfterAboutSite(location.href)}/about/`
    ).then(
      (response: AxiosResponse) => {
        getSiteInfoFail = false;
        localStorage.setItem("siteInfo", JSON.stringify(response.data));
        localStorage.setItem("integrationType", response.data.integration_type);
        setSiteInfo(response.data);
        checkArmedStatus(
          response.data,
          setArmButtonDisabled,
          navigate,
          setSiteInfo,
          setArmedStatus
        );
        setClickedTab("one");
        setActiveTab(<> Loading...</>);
        setActiveTab(
          <GeneralInfoTab
            siteInfo={response.data}
            setSiteInfo={setSiteInfo}
            setActiveTab={setActiveTab}
            vmsVariables={vmsVariables}
          />
        );
        if (response.data.auto_add) {
          localStorage.setItem("autoAdd", "true");
        } else {
          localStorage.setItem("autoAdd", "false");
        }
        // The settings are now synced to the cloud but the running connector
        // hasn't picked them up yet. On armed sites that are MEANT to be armed
        // (schedule or manual arm), offer to reboot now to apply; otherwise
        // the changes apply on the next scheduled arm. Reboot = arm, and
        // `armed` lags a stop, so gate on intent too (ENG-811 / SUP-311).
        if (shouldOfferRebootToApply(response.data)) {
          if (setRebootDialogOpen) {
            setRebootDialogOpen(true);
          } else {
            // Caller can't open the dialog from here — arm the one-shot flag
            // so the About page offers the reboot on the next landing instead
            // of silently leaving the connector on stale settings.
            localStorage.setItem("rebootSite", "true");
          }
        }
      },
      (reason: AxiosError) => {
        getSiteInfoFail = true;
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          setBackdropLoadingScreenOn(false);
        });
      }
    ));
  !deployNextFail &&
    !getSiteInfoFail &&
    (await AxiosUI.get(
      `schedule/by_customer/${getNumbersAfterAboutSite(location.href)}/`
    ).then(
      (response: AxiosResponse) => {
        getSiteScheduleFail = false;
        localStorage.setItem("siteSchedule", JSON.stringify(response.data));
        localStorage.setItem("siteId", localStorage.siteId);
        setAllSiteSchedules(response.data);
        setAlertState((previousState: AlertState) => {
          return {
            ...previousState,
            changeSuccess: false,
            activateSuccess: false,
            deactivateSuccess: false,
            disarmSuccess: false,
            armSuccess: false,
            syncSuccess: true,
            cameraDeleteSuccess: false,
            armFail: false,
          };
        });
      },
      (reason: AxiosError) => {
        getSiteScheduleFail = true;
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          setBackdropLoadingScreenOn(false);
        });
      }
    ));
  !deployNextFail &&
    !getSiteInfoFail &&
    !getSiteScheduleFail &&
    (await AxiosUI.get(
      `camera/site/?customer__id=${getNumbersAfterAboutSite(location.href)}`
    ).then(
      (response: AxiosResponse) => {
        getSiteCamerasFail = false;
        localStorage.setItem("siteCameras", JSON.stringify(response.data));
        setSiteCameras(response.data);
        // checkIfAllCamerasDecativated(
        //   response.data,
        //   setDeativateInquiryDialogOpen
        // );
      },
      (reason: AxiosError) => {
        getSiteCamerasFail = true;
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          setBackdropLoadingScreenOn(false);
        });
      }
    ));
  !deployNextFail &&
    !getSiteInfoFail &&
    !getSiteScheduleFail &&
    !getSiteCamerasFail &&
    (await AxiosUI.get(
      `nvr_camera/?customer__id=${getNumbersAfterAboutSite(location.href)}`
    ).then(
      (response: AxiosResponse) => {
        if (response.data.count === 0) {
          localStorage.setItem("noAutoAddCamerasDialogOpen", "true");
        }
        if (response.data.count > 0) {
          localStorage.setItem("noAutoAddCamerasDialogOpen", "false");
        }
        getNVRCamerasFail = false;
      },
      (reason: AxiosError) => {
        getNVRCamerasFail = true;
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {});
      }
    ));
  !deployNextFail &&
    !getSiteInfoFail &&
    !getSiteScheduleFail &&
    !getSiteCamerasFail &&
    !getNVRCamerasFail &&
    (await AxiosUI.get(
      `nvr_camera/extra_fields/?customer__id=${getNumbersAfterAboutSite(
        location.href
      )}`
    ).then(
      (response: AxiosResponse) => {
        // setBackdropLoadingScreenOn(false);
        localStorage.setItem(
          "additionalFieldNamesArray",
          JSON.stringify(response.data)
        );
      },
      (reason: AxiosError) => {
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          setBackdropLoadingScreenOn(false);
        });
      }
    ));
};
